<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { persistFinanceTab } from '../composables/realEstateFinanceNavigation';
import RealEstateFinanceInstallmentActions from '../components/RealEstateFinanceInstallmentActions.vue';
import RealEstateFinanceInstallmentContent from '../components/RealEstateFinanceInstallmentContent.vue';
import RealEstateFinanceInstallmentContextHeader from '../components/RealEstateFinanceInstallmentContextHeader.vue';
import { useRealEstateFinanceInstallment } from '../composables/useRealEstateFinanceInstallment';
import { useRealEstateFinanceTransferReceipts } from '../composables/useRealEstateFinanceTransferReceipts';
import { useSalesCommissionSheetDownload } from '../composables/useSalesCommissionSheetDownload';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const installmentId = computed(() => route.params.installmentId);
const finance = useRealEstateFinanceInstallment({
  installmentId,
  notify: useAlert,
  t,
});
const { downloadTransferReceipt } = useRealEstateFinanceTransferReceipts({
  notify: useAlert,
  t,
});
const { download: downloadCommissionSheet } = useSalesCommissionSheetDownload({
  t,
});
const requestedAction = ref(null);

const backToFinance = () =>
  router.push({
    name: 'operacao_imobiliaria_financeiro',
    params: { accountId: route.params.accountId },
  });

const openCanceledTab = () => {
  persistFinanceTab('cancelado');
  backToFinance();
};

const openProcess = salesTransactionId =>
  router.push({
    name: 'operacao_imobiliaria_financeiro_detalhes',
    params: { accountId: route.params.accountId, salesTransactionId },
  });

const openInstallment = nextInstallmentId =>
  router.push({
    name: 'operacao_imobiliaria_financeiro_status',
    params: {
      accountId: route.params.accountId,
      installmentId: nextInstallmentId,
      salesTransactionId: route.params.salesTransactionId,
    },
  });

const beneficiaryType = beneficiary =>
  beneficiary.tipo || (beneficiary.empresa ? 'empresa' : 'usuario');
const beneficiaryId = beneficiary =>
  beneficiaryType(beneficiary) === 'empresa'
    ? beneficiary.empresa?.id || beneficiary.empresa_id
    : beneficiary.usuario?.id || beneficiary.usuario_id;
const openTransfer = beneficiary =>
  router.push({
    name: 'operacao_imobiliaria_financeiro_transferir',
    params: {
      accountId: route.params.accountId,
      beneficiaryId: beneficiaryId(beneficiary),
      beneficiaryType: beneficiaryType(beneficiary),
      installmentId: installmentId.value,
      salesTransactionId: route.params.salesTransactionId,
    },
  });

const requestAction = (type, payload) => {
  requestedAction.value = { payload, type };
};

const downloadSheet = sheet =>
  downloadCommissionSheet({
    artifactId: sheet.id,
    filename: sheet.filename,
    installmentId: installmentId.value,
  });

const clearAction = () => {
  requestedAction.value = null;
  if (!['status', 'cancel'].includes(route.query.action)) return;

  const query = { ...route.query };
  delete query.action;
  router.replace({ name: route.name, params: route.params, query });
};

watch(
  [() => route.query.action, finance.selectedInstallment],
  ([action, installment]) => {
    if (['status', 'cancel'].includes(action) && installment) {
      requestAction(action);
    }

    const processId = installment?.dados_processo?.processo_id;
    if (
      processId &&
      String(processId) !== String(route.params.salesTransactionId)
    ) {
      router.replace({
        name: 'operacao_imobiliaria_financeiro_status',
        params: {
          accountId: route.params.accountId,
          salesTransactionId: processId,
          installmentId: installmentId.value,
        },
        query: route.query,
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <LaisHeader
      :title="t('REAL_ESTATE_FINANCE.STATUS_PAGE.TITLE')"
      :subtitle="t('REAL_ESTATE_FINANCE.STATUS_PAGE.SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_FINANCE.TITLE') },
        { label: t('REAL_ESTATE_FINANCE.STATUS_PAGE.TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_FINANCE.SALE_DETAIL.BACK')"
      @back="backToFinance"
    />
    <main
      class="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <p
        v-if="finance.error.value"
        class="rounded-lais border border-lais-danger/30 bg-white p-4 text-sm text-lais-danger"
      >
        {{ finance.error.value }}
      </p>
      <template v-else>
        <RealEstateFinanceInstallmentContextHeader
          :address="finance.selectedInstallment.value?.parcela?.endereco"
          :loading="finance.loadingDetail.value"
          :process="finance.selectedInstallment.value?.dados_processo"
        />
        <RealEstateFinanceInstallmentContent
          :installment="finance.selectedInstallment.value"
          :loading="finance.loadingDetail.value"
          @status="requestAction('status')"
          @cancel="requestAction('cancel')"
          @payment="requestAction('payment', $event)"
          @boleto="requestAction('boleto', $event)"
          @edit-payers="requestAction('payers')"
          @open-process="openProcess"
          @open-installment="openInstallment"
          @release="requestAction('release', $event)"
          @transfer="openTransfer"
          @download-transfer="downloadTransferReceipt"
          @download-sheet="downloadSheet"
        />
      </template>
    </main>
    <RealEstateFinanceInstallmentActions
      :finance="finance"
      :requested-action="requestedAction"
      @clear="clearAction"
      @open-canceled="openCanceledTab"
    />
  </LaisGamificationSurface>
</template>
